import { X, Globe, ChevronRight, ClipboardList } from 'lucide-react';
import { FaWhatsapp, FaInstagram, FaFacebook, FaHeart, FaShoppingCart } from "react-icons/fa";
import { useTranslation } from 'context/LanguageContext';
import { useNavigate } from 'react-router-dom';

export default function SlideMenu({ isOpen, onClose }) {
  const { language, t, toggleLanguage } = useTranslation();
  const isRTL = language === 'ar';
  const navigate = useNavigate();

  const openOrders = () => {
    onClose();
    navigate('/orders');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      ></div>

      {/* Sidebar - Desktop: Centered, Mobile: Side slide */}
      <div className={`fixed top-0 ${isRTL ? 'right-0' : 'left-0'} lg:left-1/2 lg:right-auto lg:-translate-x-1/2 h-full bg-white shadow-2xl z-50 overflow-y-auto w-80 lg:w-[600px] lg:rounded-lg lg:h-auto lg:top-1/2 lg:-translate-y-1/2 lg:max-h-[90vh]`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b border-gray-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <h2 className="text-2xl font-bold text-gray-900">{t('menu')}</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Menu Content */}
        <div className={`p-6 ${isRTL ? 'text-right' : 'text-left'}`}>
          {/* Desktop: 2-column grid, Mobile: single column */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div>
              {/* Language Selection */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('language')}</h3>
                <button 
                  onClick={toggleLanguage}
                  className={`w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-900 font-medium">
                      {language === 'en' ? 'English' : 'العربية'}
                    </span>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Quick Links */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('quickLinks')}</h3>
                <button onClick={openOrders} className={`w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-900 font-medium">{t('myOrders')}</span>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
                </button>
                <button className={`w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <FaHeart className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-900 font-medium">{t('favorites')}</span>
                  </div>
                  <span className="bg-blue-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">0</span>
                </button>

                <button className={`w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <FaShoppingCart className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-900 font-medium">{t('cart')}</span>
                  </div>
                  <span className="bg-blue-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">0</span>
                </button>
              </div>
            </div>

            {/* Right Column */}
            <div>
              {/* Social Media */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('followUs')}</h3>
                <div className={`flex gap-3 ${isRTL ? 'justify-end' : ''}`}>
                  <a href="#" className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center hover:opacity-80 transition">
                    <FaFacebook className='text-white h-6 w-6' />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center hover:opacity-80 transition">
                    <FaInstagram className='text-white h-6 w-6' />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center hover:opacity-80 transition">
                    <FaWhatsapp className='text-white h-6 w-6' />
                  </a>
                </div>
              </div>

              {/* Additional Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{t('information')}</h3>
                <div className="space-y-2">
                  <a href="#" className="block p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition">
                    {t('aboutUs')}
                  </a>
                  <a href="#" className="block p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition">
                    {t('contactUs')}
                  </a>
                  <a href="#" className="block p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition">
                    {t('termsConditions')}
                  </a>
                  <a href="#" className="block p-3 text-gray-700 hover:bg-gray-50 rounded-lg transition">
                    {t('privacyPolicy')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
